import React, { useEffect, useState } from 'react'
import { Pressable, Text, View } from 'react-native'
import { Feather } from '@expo/vector-icons'
import { SelectList } from 'react-native-dropdown-select-list'
import { CheckIcon } from 'phosphor-react-native'
import {
    FormFieldsModel,
    ModelAnswerFromProps,
    SecondaryActionsModel,
} from '../models/StoreDetailModel'
import theme from '../../../../theme'
import styles from '../FormItems/DropdownItemStyles'
import { updateFormAnswerSchedule } from '../../../../db-sqlite/repositories/schedule.repository'
import { CustomCheckBox } from '../../customUtils/Components/CustomCheckBox'

// Un field puede tener su propia fila en `answers` (keyeada por su _id sintético,
// ver usePonderateGroup) o, si es el único field de la acción recién seleccionada,
// venir todavía "montado" sobre `answerProp`. Miramos ambos para no perder la
// selección guardada al reabrir el formulario.
const findSavedAnswer = (
    item: FormFieldsModel,
    answerProp?: ModelAnswerFromProps,
    answers?: ModelAnswerFromProps[],
): ModelAnswerFromProps | undefined => {
    return (
        (answers ?? []).find((a) => a && item._id && a._id === item._id) ??
        (item._id && answerProp?._id === item._id ? answerProp : undefined)
    )
}

interface DropdownFieldProps {
    item: FormFieldsModel
    form_id: string
    schedule_id: string
    store_id: string
    answerProp: ModelAnswerFromProps
    answers?: ModelAnswerFromProps[]
    checkComplete: (option?: ModelAnswerFromProps) => void
    saveItemSteps?: (option?: ModelAnswerFromProps) => void
    // Se dispara siempre, apenas se elige una opción — antes y por fuera de
    // saveItemSteps/checkComplete (ver useFieldsVisibility). Distinto flujos
    // usan uno u otro para persistir, y este además necesita enterarse ya
    // mismo para poder mostrar/ocultar fields relacionados sin esperar.
    onAnswerChange?: (answer?: ModelAnswerFromProps) => void
}

export const DropdownField = React.memo(
    ({
        item,
        form_id,
        schedule_id,
        store_id,
        answerProp,
        answers,
        checkComplete,
        saveItemSteps,
        onAnswerChange,
    }: DropdownFieldProps) => {
        const [selectedValue, setSelectedValue] = useState<SecondaryActionsModel>()
        const [options, setOptions] = useState<
            Array<{ key: string; value: string; disabled?: boolean }>
        >([])
        const [defaultOption, setDefaultOption] = useState(null)

        useEffect(() => {
            if (item.secondary_actions && item.secondary_actions.length > 0) {
                const mappedOptions = item.secondary_actions.map((option) => ({
                    key: option._id,
                    value: option.label,
                    disabled:
                        (option.status != null && option.status) === -1
                            ? true
                            : false,
                }))
                setOptions(mappedOptions)
            }
        }, [item.secondary_actions])

        useEffect(() => {
            const savedAnswer = findSavedAnswer(item, answerProp, answers)
            const optionId = savedAnswer?.option_selected_id?.[0]
            if (!optionId) return

            const findIndex = (item.secondary_actions ?? []).findIndex(
                (action) => action._id === optionId,
            )
            if (findIndex !== -1) {
                const action = item.secondary_actions[findIndex]
                setSelectedValue(action)
                setDefaultOption({ key: action._id, value: action.label })
            }
        }, [])

        const onSelectOption = (optionValue: string) => {
            const selectedOption = (item.secondary_actions ?? []).find(
                (option) => option.label === optionValue,
            )
            if (!selectedOption || selectedOption === selectedValue) {
                return
            }

            setSelectedValue(selectedOption)

            const answer: ModelAnswerFromProps = {
                _id: item._id,
                option_selected_text: [selectedOption.label],
                option_selected_id: [selectedOption._id],
                question_id: answerProp?.question_id ?? item._id ?? '',
                media: '',
                question_text: item.label || item.title || '',
                score: selectedOption.score
                    ? selectedOption.score.toString()
                    : item?.score
                      ? item.score.toString()
                      : '0',
                status: 1,
                date_finish: new Date().toISOString(),
                date_init: new Date().toISOString(),
                question_input: '',
                field_media: '',
                field_text: selectedOption.label ?? '',
                questionScore: item?.questionScore ?? 0,
                index: answerProp?.index,
                type: item.type?.type,
                ponderar: !!(item?.questionScore && item.questionScore > 0),

                question: item.label || item.title || '',
                questionOptionSelected: [selectedOption._id],
                questionOptionSelectedText: [selectedOption.label],
                questionType: (item as any).type,
                questionPrice: '',
                tag: '',
                questionImage: '',
                questionInput: '',
                sku: (answerProp as any)?.sku,
                description: (answerProp as any)?.description ?? '',
                description_short: (answerProp as any)?.description_short ?? '',
            }

            onAnswerChange?.(answer)

            if (saveItemSteps) {
                saveItemSteps(answer)
            } else {
                if (form_id) {
                    updateFormAnswerSchedule({
                        newAnswer: answer,
                        schedule_id,
                        form_id,
                        store_id,
                    })
                }
                checkComplete(answer)
            }
        }

        return (
            <View style={{ flex: 1 }}>
                {(item.title || item.label) && (
                    <Text style={styles.title}>{item.title || item.label}</Text>
                )}

                {options.length > 2 ? (
                    <SelectList
                        placeholder='Selecciona una opción'
                        maxHeight={10000}
                        data={options}
                        save='value'
                        search={false}
                        searchPlaceholder='Buscar...'
                        arrowicon={
                            <Feather
                                name={'chevron-down'}
                                size={22}
                                color={theme.colors.colorPrimary}
                            />
                        }
                        boxStyles={styles.inputWithMarginField}
                        dropdownStyles={styles.dropdownField}
                        dropdownTextStyles={styles.dropdownOptions}
                        inputStyles={
                            selectedValue
                                ? styles.dropdownInputSelected
                                : styles.dropdownInput
                        }
                        disabledItemStyles={styles.disabledDropdown}
                        disabledTextStyles={styles.disabledOptionText}
                        setSelected={onSelectOption}
                        defaultOption={defaultOption}
                    />
                ) : (
                    <CustomCheckBox
                        options={options}
                        defaultValue={defaultOption}
                        onChangeSelected={onSelectOption}
                    />
                )}
            </View>
        )
    },
)

interface DropdownMultipleSelectFieldProps {
    item: FormFieldsModel
    form_id: string
    schedule_id: string
    store_id: string
    answerProp: ModelAnswerFromProps
    answers?: ModelAnswerFromProps[]
    checkComplete: (option?: ModelAnswerFromProps) => void
    saveItemSteps?: (option?: ModelAnswerFromProps) => void
    // Ver comentario en DropdownFieldProps.
    onAnswerChange?: (answer?: ModelAnswerFromProps) => void
}

export const DropdownMultipleSelectField = React.memo(
    ({
        item,
        form_id,
        schedule_id,
        store_id,
        answerProp,
        answers,
        checkComplete,
        saveItemSteps,
        onAnswerChange,
    }: DropdownMultipleSelectFieldProps) => {
        const [selectedOptions, setSelectedOptions] = useState<string[]>([])

        useEffect(() => {
            const savedAnswer = findSavedAnswer(item, answerProp, answers)
            if (savedAnswer?.option_selected_id) {
                setSelectedOptions(savedAnswer.option_selected_id)
            }
        }, [])

        const options = (item.secondary_actions ?? []).map((option) => ({
            key: option._id,
            name: option.label,
        }))

        const persistSelection = (selectedIds: string[]) => {
            // Preserva el orden en que se fueron tocando las opciones (no el
            // orden en que vienen definidas en secondary_actions): el show/hide
            // de un checklist_multiplechoice debe respetar la última tocada,
            // y eso se identifica mirando el último elemento de option_selected_id.
            const selectedActions = selectedIds
                .map((id) =>
                    (item.secondary_actions ?? []).find((a) => a._id === id),
                )
                .filter((a): a is SecondaryActionsModel => !!a)

            const answer: ModelAnswerFromProps = {
                _id: item._id,
                option_selected_text: selectedActions.map((a) => a.label),
                option_selected_id: selectedActions.map((a) => a._id),
                question_id: answerProp?.question_id ?? item._id ?? '',
                media: '',
                question_text: item.label || item.title || '',
                score: '0',
                status: 1,
                date_finish: new Date().toISOString(),
                date_init: new Date().toISOString(),
                question_input: '',
                field_media: '',
                field_text: selectedActions.map((a) => a.label).join(', '),
                questionScore: item?.questionScore ?? 0,
                index: answerProp?.index,
                type: item.type?.type,
                ponderar: !!(item?.questionScore && item.questionScore > 0),

                question: item.label || item.title || '',
                questionOptionSelected: selectedActions.map((a) => a._id),
                questionOptionSelectedText: selectedActions.map((a) => a.label),
                questionType: (item as any).type,
                questionPrice: '',
                tag: '',
                questionImage: '',
                questionInput: '',
                sku: (answerProp as any)?.sku,
                description: (answerProp as any)?.description ?? '',
                description_short: (answerProp as any)?.description_short ?? '',
            }

            onAnswerChange?.(answer)

            if (saveItemSteps) {
                saveItemSteps(answer)
            } else {
                if (form_id) {
                    updateFormAnswerSchedule({
                        newAnswer: answer,
                        schedule_id,
                        form_id,
                        store_id,
                    })
                }
                checkComplete(answer)
            }
        }

        const onSelectedItemsChange = (key: string) => {
            setSelectedOptions((prev) => {
                const updated = prev.includes(key)
                    ? prev.filter((k) => k !== key)
                    : [...prev, key]

                persistSelection(updated)
                return updated
            })
        }

        return (
            <View style={{ flex: 1 }}>
                {(item?.title || item?.label) && (
                    <Text style={styles.title}>{item.title || item.label}</Text>
                )}

                {options.map((option) => {
                    const isSelected = selectedOptions.includes(option.key)

                    return (
                        <Pressable
                            key={option.key}
                            onPress={() => onSelectedItemsChange(option.key)}
                            style={{
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: 16,
                                marginHorizontal: 12,
                                marginVertical: 10,
                            }}
                        >
                            <View
                                style={{
                                    width: 24,
                                    height: 24,
                                    borderRadius: 4,
                                    borderWidth: 2,
                                    borderColor: !isSelected
                                        ? '#999'
                                        : theme.colors.colorPrimary,
                                    backgroundColor: !isSelected
                                        ? 'white'
                                        : theme.colors.colorPrimary,
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                {isSelected && (
                                    <CheckIcon
                                        size={18}
                                        weight='bold'
                                        color={'white'}
                                    />
                                )}
                            </View>

                            <Text
                                style={{
                                    fontSize: 15,
                                    color: theme.colors.textPrimary,
                                    flexShrink: 1,
                                }}
                            >
                                {option.name}
                            </Text>
                        </Pressable>
                    )
                })}
            </View>
        )
    },
)
